class ZoomableImage extends HTMLElement {
  static PAN_STEP_RATIO = 0.15;
  static PAN_STEP_MIN = 40;
  static TAP_MOVE_THRESHOLD = 8;

  constructor() {
    super();
    this.isEnabled = this.dataset.enabled === "true";
    this.isZoomed = false;
    this.isPanning = false;
    this.pointerDidMove = false;
    this.panX = 0;
    this.panY = 0;
    this.startX = 0;
    this.startY = 0;
    this.startPanX = 0;
    this.startPanY = 0;
    this.imageElement = this.querySelector("img");
    this.zoomButton = this.querySelector(".product-gallery-zoom-icon");

    if (!this.imageElement) return;

    this.originalImageSrc = this.imageElement.getAttribute("src") ?? "";
    const widthMatch = this.originalImageSrc.match(/[?&]width=(\d+)/);
    this.originalImageWidth = widthMatch ? Number(widthMatch[1]) : 0;
    this.zoomedImageWidth = this.originalImageWidth * 2;
    this.zoomedImageSrc = this.originalImageSrc.replace(
      /width=(\d+)/,
      `width=${this.zoomedImageWidth}`,
    );

    this.imageElement.setAttribute("draggable", "false");

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onOutsideClick = this.onOutsideClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.render();
    this.setupEventHandlers();
  }

  close() {
    if (!this.isEnabled || !this.isZoomed) return;
    this.isZoomed = false;
    this.isPanning = false;
    this.pointerDidMove = false;
    this.resetPan();
    this.switchImageSizing();
    this.unbindOutsideClose();
    this.notifyToggle();
  }

  open() {
    if (!this.isEnabled || this.isZoomed) return;
    this.isZoomed = true;
    this.switchImageSizing();
    requestAnimationFrame(() => this.centerPan());
    this.bindOutsideClose();
    this.notifyToggle();
  }

  bindOutsideClose() {
    // Defer so the click that opened zoom does not immediately close it.
    requestAnimationFrame(() => {
      if (!this.isZoomed) return;
      document.addEventListener("click", this.onOutsideClick, true);
    });
  }

  unbindOutsideClose() {
    document.removeEventListener("click", this.onOutsideClick, true);
  }

  onOutsideClick(event) {
    if (!this.isZoomed) return;
    if (this.contains(event.target)) return;
    this.close();
  }

  onKeyDown(event) {
    if (!this.isEnabled || !this.isZoomed) return;

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }

    if (!this.contains(document.activeElement)) return;

    const step = Math.max(
      ZoomableImage.PAN_STEP_MIN,
      Math.round(Math.min(this.clientWidth, this.clientHeight) * ZoomableImage.PAN_STEP_RATIO),
    );

    let deltaX = 0;
    let deltaY = 0;

    switch (event.key) {
      case "ArrowLeft":
        deltaX = step;
        break;
      case "ArrowRight":
        deltaX = -step;
        break;
      case "ArrowUp":
        deltaY = step;
        break;
      case "ArrowDown":
        deltaY = -step;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.panBy(deltaX, deltaY);
  }

  notifyToggle() {
    if (!this.isEnabled) return;
    document.dispatchEvent(
      new CustomEvent("zoomable-image:toggle", {
        detail: { isZoomed: this.isZoomed },
      }),
    );
  }

  switchImageSizing() {
    this.classList.toggle("gallery-image--zoomed", this.isZoomed);
    this.imageElement.setAttribute(
      "src",
      this.isZoomed ? this.zoomedImageSrc : this.originalImageSrc,
    );

    if (this.zoomButton) {
      this.zoomButton.setAttribute(
        "aria-label",
        this.isZoomed
          ? "Zoom out. Arrow keys pan. Tap image to center on that point."
          : "Zoom in",
      );
      this.zoomButton.setAttribute("aria-pressed", String(this.isZoomed));
    }
  }

  resetPan() {
    this.panX = 0;
    this.panY = 0;
    this.applyPan();
  }

  centerPan() {
    const bounds = this.getPanBounds();
    this.panX = bounds.minX / 2;
    this.panY = bounds.minY / 2;
    this.applyPan();
  }

  panBy(deltaX, deltaY) {
    const clamped = this.clampPan(this.panX + deltaX, this.panY + deltaY);
    this.panX = clamped.x;
    this.panY = clamped.y;
    this.applyPan();
  }

  // Single-pointer alternative to dragging: center the view on a local point.
  panToLocalPoint(localX, localY) {
    const clamped = this.clampPan(
      this.panX + (this.clientWidth / 2 - localX),
      this.panY + (this.clientHeight / 2 - localY),
    );
    this.panX = clamped.x;
    this.panY = clamped.y;
    this.applyPan();
  }

  getPanBounds() {
    // scale(2) with transform-origin 0 0 doubles visual size without
    // growing the wrapper, so pan room is exactly one container side.
    const scale = 2;
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;

    return {
      minX: containerWidth * (1 - scale),
      maxX: 0,
      minY: containerHeight * (1 - scale),
      maxY: 0,
    };
  }

  clampPan(x, y) {
    const bounds = this.getPanBounds();
    return {
      x: Math.min(bounds.maxX, Math.max(bounds.minX, x)),
      y: Math.min(bounds.maxY, Math.max(bounds.minY, y)),
    };
  }

  applyPan() {
    this.style.setProperty("--zoom-x", `${this.panX}px`);
    this.style.setProperty("--zoom-y", `${this.panY}px`);
  }

  onPointerDown(event) {
    if (!this.isZoomed || !this.isEnabled) return;
    if (event.target.closest(".product-gallery-zoom-icon")) return;
    if (event.button !== undefined && event.button !== 0) return;

    this.isPanning = true;
    this.pointerDidMove = false;
    this.classList.add("is-panning");
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startPanX = this.panX;
    this.startPanY = this.panY;
    this.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  onPointerMove(event) {
    if (!this.isPanning) return;

    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;

    if (
      !this.pointerDidMove &&
      Math.hypot(deltaX, deltaY) >= ZoomableImage.TAP_MOVE_THRESHOLD
    ) {
      this.pointerDidMove = true;
    }

    if (!this.pointerDidMove) return;

    const clamped = this.clampPan(
      this.startPanX + deltaX,
      this.startPanY + deltaY,
    );
    this.panX = clamped.x;
    this.panY = clamped.y;
    this.applyPan();
  }

  onPointerUp(event) {
    if (!this.isPanning) return;

    const wasTap = !this.pointerDidMove;
    this.isPanning = false;
    this.pointerDidMove = false;
    this.classList.remove("is-panning");

    if (!wasTap || !this.isZoomed) return;
    if (event.target.closest(".product-gallery-zoom-icon")) return;

    const rect = this.getBoundingClientRect();
    this.panToLocalPoint(event.clientX - rect.left, event.clientY - rect.top);
  }

  setupEventHandlers() {
    if (this.zoomButton) {
      this.zoomButton.addEventListener("click", (event) => {
        event.stopPropagation();
        if (this.isZoomed) {
          this.close();
        } else {
          this.open();
        }
      });
    }

    this.addEventListener("pointerdown", this.onPointerDown);
    this.addEventListener("pointermove", this.onPointerMove);
    this.addEventListener("pointerup", this.onPointerUp);
    this.addEventListener("pointercancel", this.onPointerUp);

    document.addEventListener("keydown", this.onKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.onKeyDown);
    this.unbindOutsideClose();

    if (this.isZoomed) {
      this.close();
    }
  }

  render() {
    if (this.isEnabled) {
      this.classList.add("gallery-image--zoomable");
    }
  }
}

customElements.define("zoomable-image", ZoomableImage);
